import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { portalErrorMessage } from '../portalHttp';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate, formatDateTime, toLocalIsoDate } from '../../utils/dateUtils';
import { SupplierBillRequestRow, SupplierPoRow, SupplierPortalApi } from './supplierPortalApi';

// ---- F8 supplier portal: bill requests (supplier sends an invoice; staff review and convert it to a bill) ----
export const MAX_BILL_FILE_BYTES = 5 * 1024 * 1024;
const ACCEPT = ['application/pdf', 'image/png', 'image/jpeg'];
const color = (s: string): 'default' | 'info' | 'success' | 'error' | 'warning' =>
  s === 'CONVERTED' ? 'success' : s === 'REJECTED' ? 'error' : s === 'UNDER_REVIEW' ? 'info' : s === 'WITHDRAWN' ? 'default' : 'warning';

const SubmitDialog: React.FC<{ open: boolean; onClose: () => void; onDone: () => void }> = ({ open, onClose, onDone }) => {
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(toLocalIsoDate(new Date()));
  const [amount, setAmount] = useState('');
  const [poId, setPoId] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [pos, setPos] = useState<SupplierPoRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    SupplierPortalApi.pos(0, 100).then(p => setPos(p.rows)).catch(() => setPos([]));
  }, [open]);

  const pick = (f: File | null) => {
    setError(null);
    if (f && !ACCEPT.includes(f.type)) { setError('Only PDF, PNG or JPEG files are accepted.'); setFile(null); return; }
    if (f && f.size > MAX_BILL_FILE_BYTES) { setError('The file is larger than 5 MB.'); setFile(null); return; }
    setFile(f);
  };
  const valid = !!file && invoiceNo.trim() && invoiceDate && Number(amount) > 0;
  const submit = async () => {
    if (!file || !valid) return;
    setBusy(true); setError(null);
    try {
      await SupplierPortalApi.submitBillRequest({
        file, invoiceNo: invoiceNo.trim(), invoiceDate, amount, purchaseOrderId: poId || undefined,
        description: description.trim() || undefined,
      });
      setInvoiceNo(''); setAmount(''); setPoId(''); setDescription(''); setFile(null);
      if (input.current) input.current.value = '';
      onDone();
    } catch (e) {
      setError(portalErrorMessage(e, 'Could not send the bill request'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Send an invoice</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Your invoice number" required fullWidth value={invoiceNo} inputProps={{ maxLength: 60 }}
            onChange={e => setInvoiceNo(e.target.value)} />
          <TextField label="Invoice date" type="date" required fullWidth value={invoiceDate} InputLabelProps={{ shrink: true }}
            onChange={e => setInvoiceDate(e.target.value)} />
          <TextField label="Amount" type="number" required fullWidth value={amount} inputProps={{ min: 0, step: '0.01' }}
            onChange={e => setAmount(e.target.value)} />
          <TextField select label="Purchase order (optional)" fullWidth value={poId} onChange={e => setPoId(e.target.value)}>
            <MenuItem value="">None</MenuItem>
            {pos.map(p => <MenuItem key={p.id} value={p.id}>{p.poNumber}</MenuItem>)}
          </TextField>
          <TextField label="Description" fullWidth multiline minRows={2} value={description} inputProps={{ maxLength: 1000 }}
            onChange={e => setDescription(e.target.value)} />
          <Box>
            <Typography variant="body2" sx={{ mb: 0.5 }}>Invoice file (PDF, PNG or JPEG, up to 5 MB)</Typography>
            <input ref={input} type="file" aria-label="Invoice file" accept={ACCEPT.join(',')}
              onChange={e => pick(e.target.files?.[0] ?? null)} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid || busy} onClick={submit}>Send</Button>
      </DialogActions>
    </Dialog>
  );
};

const SupplierBillRequests: React.FC = () => {
  const [rows, setRows] = useState<SupplierBillRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try { setRows(await SupplierPortalApi.billRequests()); setError(null); }
    catch (e) { setError(portalErrorMessage(e, 'Could not load your bill requests')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const withdraw = async (r: SupplierBillRequestRow) => {
    setError(null); setOkMsg(null);
    try { await SupplierPortalApi.withdraw(r.id); setOkMsg(`${r.requestNo} withdrawn.`); await load(); }
    catch (e) { setError(portalErrorMessage(e, 'Could not withdraw this request')); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Bill requests</Typography>
        <Button variant="contained" onClick={() => setOpen(true)}>Send an invoice</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {okMsg && <Alert severity="success" sx={{ mb: 2 }}>{okMsg}</Alert>}
      {loading ? <CircularProgress /> : (
        <Paper><TableContainer><Table size="small">
          <TableHead><TableRow>
            <TableCell>Request</TableCell><TableCell>Invoice</TableCell><TableCell>Invoice date</TableCell>
            <TableCell align="right">Amount</TableCell><TableCell>PO</TableCell><TableCell>Status</TableCell>
            <TableCell>Comment</TableCell><TableCell>Sent</TableCell><TableCell />
          </TableRow></TableHead>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={9} align="center">No bill requests yet.</TableCell></TableRow>}
            {rows.map(r => (
              <TableRow key={r.id}>
                <TableCell>{r.requestNo}</TableCell>
                <TableCell>{r.supplierInvoiceNo}</TableCell>
                <TableCell>{formatDate(r.invoiceDate)}</TableCell>
                <TableCell align="right">{formatMoney(r.amount)}</TableCell>
                <TableCell>{r.poNumber ?? ''}</TableCell>
                <TableCell><Chip size="small" label={humanizeEnum(r.status)} color={color(r.status)} /></TableCell>
                <TableCell>{r.reviewComment ?? ''}</TableCell>
                <TableCell>{formatDateTime(r.submittedAt)}</TableCell>
                <TableCell>
                  {r.status === 'SUBMITTED' && <Button size="small" color="warning" onClick={() => withdraw(r)}>Withdraw</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table></TableContainer></Paper>
      )}
      <SubmitDialog open={open} onClose={() => setOpen(false)}
        onDone={() => { setOpen(false); setOkMsg('Sent. The buyer will review your invoice.'); load(); }} />
    </Box>
  );
};

export default SupplierBillRequests;
// ---- end F8 ----
